import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search, X, TrendingUp, Clock, ChevronRight,
  ChevronLeft, ArrowUpRight, Flame,
} from "lucide-react";
import { fetchNewsList, SECTORS_21 } from "../../utils/api/newsApi.js";
import { getImageUrl } from "../../utils/imageUtils.js";
import { useLanguage } from "../../context/LanguageContext.jsx";

/* ─── helpers ─────────────────────────────────────── */
const SECTOR_COLORS = {
  'शहरी योजना तथा भौतिक पूर्वाधार': { color: '#d97706', bg: '#fffbeb' },
  'सुरक्षा':                          { color: '#dc2626', bg: '#fef2f2' },
  'खानेपानी':                         { color: '#0891b2', bg: '#ecfeff' },
  'फोहोर तथा प्रदूषण व्यवस्थापन':    { color: '#65a30d', bg: '#f7fee7' },
  'वन, वातावरण र पर्यटन':             { color: '#16a34a', bg: '#f0fdf4' },
  'शिक्षा, अनुसन्धान र ज्ञान':        { color: '#7c3aed', bg: '#f5f3ff' },
  'शिल्प, सीप तथा उद्यमशीलता':       { color: '#b45309', bg: '#fef3c7' },
  'स्वास्थ्य तथा जनस्वास्थ्य':        { color: '#dc2626', bg: '#fef2f2' },
  'स्वस्थ जीवनशैली र उपभोक्ता हित':  { color: '#0d9488', bg: '#f0fdfa' },
  'खेलकुद तथा खेल प्रशिक्षण':        { color: '#ea580c', bg: '#fff7ed' },
  'सूचना प्रविधि तथा स्मार्ट सिटी':  { color: '#0ea5e9', bg: '#f0f9ff' },
  'मिडिया तथा सञ्चार':                { color: '#6366f1', bg: '#eef2ff' },
  'व्यापार, उद्योग र आर्थिक विकास':  { color: '#059669', bg: '#ecfdf5' },
  'कृषि तथा सहकारी':                  { color: '#4d7c0f', bg: '#f7fee7' },
  'विपद् व्यवस्थापन':                 { color: '#9f1239', bg: '#fff1f2' },
  'कला, संस्कृति र पहिचान':           { color: '#db2777', bg: '#fdf2f8' },
  'सुशासन, आर्थिक नीति र वित्तीय व्यवस्थापन': { color: '#1e3a8a', bg: '#eff6ff' },
  'सामाजिक सुरक्षा र समावेशी विकास': { color: '#7c3aed', bg: '#f5f3ff' },
  'महिला, बालबालिका र युवा विकास':    { color: '#be185d', bg: '#fdf2f8' },
  'अन्तर्राष्ट्रिय सम्बन्ध':          { color: '#0369a1', bg: '#f0f9ff' },
};

const getSectorMeta = (sector) =>
  SECTOR_COLORS[sector] || { color: '#166534', bg: '#f0fdf4' };

const PAGE_SIZE = 6;

const CONTENT = {
  ne: {
    featuredBadge: "विशेष",
    readFull: "पूरा पढ्नुहोस्",
    readMore: "थप पढ्नुहोस्",
    trendingNow: "ट्रेन्डिंग समाचार",
    recentNews: "हालका समाचार",
    loadError: "समाचार लोड गर्न सकिएन। पछि फेरि प्रयास गर्नुहोस्।",
    loading: "समाचार लोड हुँदैछ…",
    eyebrow: "भोलिको इटहरी · समाचार कक्ष",
    title1: "समाचार तथा",
    title2: "अपडेटहरू",
    desc: "इटहरीका विकास क्षेत्रका ताजा समाचार र प्रगति अपडेट।",
    statTotal: "कुल समाचार",
    statSectors: "क्षेत्रहरू",
    statUpdateValue: "दैनिक",
    statUpdate: "अपडेट",
    searchPlaceholder: "समाचार खोज्नुहोस्…",
    newsCountSuffix: "समाचार",
    all: "सबै",
    noResultsTitle: "समाचार फेला परेन",
    noResultsBody: "फरक keyword वा sector छान्नुहोस्",
    reset: "Reset गर्नुहोस्",
  },
  en: {
    featuredBadge: "Featured",
    readFull: "Read Full Story",
    readMore: "Read More",
    trendingNow: "Trending Now",
    recentNews: "Recent News",
    loadError: "Could not load news. Please try again later.",
    loading: "Loading news…",
    eyebrow: "Bholiko Itahari · Newsroom",
    title1: "News &",
    title2: "Updates",
    desc: "Latest news and progress updates across Itahari's development sectors.",
    statTotal: "Total News",
    statSectors: "Sectors",
    statUpdateValue: "Daily",
    statUpdate: "Updates",
    searchPlaceholder: "Search news…",
    newsCountSuffix: "articles",
    all: "All",
    noResultsTitle: "No news found",
    noResultsBody: "Try a different keyword or sector",
    reset: "Reset",
  },
};

/* ─── SectorBadge ─────────────────────────────────── */
function SectorBadge({ sector, small }) {
  const m = getSectorMeta(sector);
  return (
    <span
      className="inline-flex items-center rounded-md font-medium"
      style={{
        background: m.bg,
        color: m.color,
        fontSize: small ? '0.62rem' : '0.7rem',
        padding: small ? '2px 7px' : '3px 9px',
        border: `1px solid ${m.color}25`,
      }}
    >
      {sector}
    </span>
  );
}

/* ─── FeaturedCard ────────────────────────────────── */
function FeaturedCard({ article, t }) {
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={`/news/${article._id}`}
      className="block relative rounded-2xl overflow-hidden border transition-all duration-300 no-underline"
      style={{
        borderColor: hov ? 'rgba(22,101,52,0.3)' : '#e2e8f0',
        boxShadow: hov ? '0 20px 48px rgba(22,101,52,0.12)' : '0 2px 8px rgba(0,0,0,0.05)',
        transform: hov ? 'translateY(-3px)' : 'none',
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div className="relative h-72 sm:h-96 overflow-hidden bg-gray-200">
        {article.imageUrl ? (
          <img
            src={getImageUrl(article.imageUrl)}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500"
            style={{ transform: hov ? 'scale(1.04)' : 'scale(1)' }}
          />
        ) : (
          <div className="w-full h-full bg-green-900 flex items-center justify-center">
            <span className="text-white/30 text-5xl">📰</span>
          </div>
        )}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 30%, rgba(10,22,40,0.88) 100%)' }} />

        <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white"
            style={{ background: '#dc2626', boxShadow: '0 2px 8px rgba(220,38,38,0.4)' }}>
            <Flame className="w-3 h-3" /> {t.featuredBadge}
          </span>
          <SectorBadge sector={article.sector} />
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
          <h2 className="text-white font-bold leading-tight mb-3"
            style={{ fontSize: 'clamp(1.2rem,2.5vw,1.75rem)', maxWidth: '700px' }}>
            {article.title}
          </h2>
          <p className="text-white/65 mb-5 max-w-2xl line-clamp-2 text-[0.85rem] leading-relaxed">
            {article.excerpt}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="ml-auto flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white"
              style={{ background: '#166534', boxShadow: '0 4px 16px rgba(22,101,52,0.4)' }}>
              {t.readFull} <ArrowUpRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

/* ─── NewsCard ────────────────────────────────────── */
function NewsCard({ article, t }) {
  const [hov, setHov] = useState(false);
  const m = getSectorMeta(article.sector);

  return (
    <Link
      to={`/news/${article._id}`}
      className="rounded-2xl border overflow-hidden flex flex-col transition-all duration-200 no-underline"
      style={{
        background: 'white',
        borderColor: hov ? `${m.color}30` : '#e2e8f0',
        boxShadow: hov ? '0 12px 32px rgba(0,0,0,0.09)' : '0 1px 4px rgba(0,0,0,0.04)',
        transform: hov ? 'translateY(-4px)' : 'none',
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div className="h-0.5 transition-all duration-300"
        style={{ background: hov ? m.color : 'transparent' }} />

      <div className="relative h-44 overflow-hidden bg-gray-100">
        {article.imageUrl ? (
          <img
            src={getImageUrl(article.imageUrl)}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500"
            style={{ transform: hov ? 'scale(1.06)' : 'scale(1)' }}
          />
        ) : (
          <div className="w-full h-full bg-green-900 flex items-center justify-center">
            <span className="text-white/30 text-3xl">📰</span>
          </div>
        )}
        <div className="absolute top-3 left-3">
          <SectorBadge sector={article.sector} />
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-semibold leading-snug mb-2 flex-1 text-[0.92rem] text-gray-900"
          style={{ lineHeight: 1.45 }}>
          {article.title}
        </h3>
        <p className="mb-4 line-clamp-2 text-[0.76rem] text-gray-500" style={{ lineHeight: 1.65 }}>
          {article.excerpt}
        </p>

        <div
          className="mt-4 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all duration-200"
          style={{
            background: hov ? m.color : '#f0fdf4',
            color: hov ? 'white' : '#166534',
          }}
        >
          {t.readMore} <ChevronRight className="w-3.5 h-3.5" />
        </div>
      </div>
    </Link>
  );
}

/* ─── TrendingItem ────────────────────────────────── */
function TrendingItem({ article, rank }) {
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={`/news/${article._id}`}
      className="flex gap-3 no-underline group"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <span
        className="shrink-0 w-6 h-6 rounded-lg flex items-center justify-center font-bold mt-0.5 transition-colors text-[0.65rem]"
        style={{
          background: rank === 1 ? '#dc2626' : hov ? '#f0fdf4' : '#f3f4f6',
          color: rank === 1 ? 'white' : '#6b7280',
        }}
      >
        {rank}
      </span>
      <div className="flex-1 min-w-0">
        <p className="leading-snug line-clamp-2 transition-colors text-[0.77rem] font-medium"
          style={{ color: hov ? '#166534' : '#111827', lineHeight: 1.45 }}>
          {article.title}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <SectorBadge sector={article.sector} small />
        </div>
      </div>
    </Link>
  );
}

/* ─── Sidebar ─────────────────────────────────────── */
function Sidebar({ trending, recent, t }) {
  return (
    <aside className="w-full lg:w-72 shrink-0 space-y-5">

      <div className="rounded-2xl border border-slate-200 p-5 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center">
            <TrendingUp className="w-3.5 h-3.5 text-green-800" />
          </div>
          <p className="font-semibold text-[0.85rem] text-gray-900">{t.trendingNow}</p>
        </div>
        <div className="space-y-3">
          {trending.map((a, i) => (
            <TrendingItem key={a._id} article={a} rank={i + 1} />
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 p-5 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center">
            <Clock className="w-3.5 h-3.5 text-red-500" />
          </div>
          <p className="font-semibold text-[0.85rem] text-gray-900">{t.recentNews}</p>
        </div>
        <div className="space-y-4">
          {recent.map((a) => (
            <Link key={a._id} to={`/news/${a._id}`} className="flex gap-3 group no-underline">
              <div className="w-16 h-14 rounded-lg overflow-hidden shrink-0 bg-gray-100">
                {a.imageUrl ? (
                  <img src={getImageUrl(a.imageUrl)} alt={a.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full bg-green-900" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="leading-snug line-clamp-2 group-hover:text-green-800 transition-colors text-[0.75rem] font-semibold text-gray-900"
                  style={{ lineHeight: 1.4 }}>
                  {a.title}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <SectorBadge sector={a.sector} small />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </aside>
  );
}

/* ─── Main NewsPage ───────────────────────────────── */
export default function NewsPage() {
  const { language } = useLanguage();
  const t = CONTENT[language];

  const [allNews, setAllNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [activeSector, setActiveSector] = useState('All');
  const [page, setPage] = useState(1);


  useEffect(() => {
    const loadNews = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchNewsList();
        setAllNews(Array.isArray(data) ? data : (data?.data ?? []));
      } catch (err) {
        console.error("Error fetching news:", err);
        setError(t.loadError);
      } finally {
        setLoading(false);
      }
    };
    loadNews();
  }, [language]);

  const featured = useMemo(
    () => allNews.find((a) => a.featured) || allNews[0],
    [allNews]
  );

  const trending = useMemo(
    () => [...allNews].sort((a, b) => (b.views ?? 0) - (a.views ?? 0)).slice(0, 5),
    [allNews]
  );

  const recent = useMemo(
    () => [...allNews].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 4),
    [allNews]
  );

  const filtered = useMemo(() => {
    if (!featured) return [];
    return allNews.filter(a => a._id !== featured._id).filter(a => {
      const q = query.toLowerCase();
      const matchQ = !q || a.title.toLowerCase().includes(q) || a.excerpt.toLowerCase().includes(q);
      const matchS = activeSector === 'All' || a.sector === activeSector;
      return matchQ && matchS;
    });
  }, [allNews, query, activeSector, featured]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSector = (s) => { setActiveSector(s); setPage(1); };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-gray-400 text-sm">{t.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-4xl mb-3">⚠️</p>
          <p className="text-gray-700 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">

      {/* Header */}
      <div className="bg-green-900 pt-24 pb-14 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <div>
              <p className="text-orange-400 text-xs tracking-widest uppercase font-semibold mb-3">
                {t.eyebrow}
              </p>
              <h1 className="text-white font-bold leading-tight mb-2"
                style={{ fontSize: 'clamp(2rem,4vw,3.2rem)' }}>
                {t.title1}<br />
                <span className="text-orange-400">{t.title2}</span>
              </h1>
              <p className="text-white/45 text-[0.9rem]" style={{ lineHeight: 1.7 }}>
                {t.desc}
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              {[
                { v: String(allNews.length), l: t.statTotal },
                { v: '21', l: t.statSectors },
                { v: t.statUpdateValue, l: t.statUpdate },
              ].map(({ v, l }) => (
                <div key={l} className="rounded-xl px-4 py-3 border text-center"
                  style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                  <p className="text-white font-bold text-[1.35rem] leading-none">{v}</p>
                  <p className="text-[0.65rem] text-white/35 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky search + filter */}
      <div className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6">

          <div className="flex items-center gap-4 py-3 border-b border-slate-100">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder={t.searchPlaceholder}
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                className="w-full pl-9 pr-8 py-2 rounded-xl border border-slate-200 outline-none text-[0.83rem] text-gray-900 bg-white focus:ring-2 focus:ring-green-100 focus:border-green-300 transition-all"
              />
              {query && (
                <button onClick={() => setQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                  <X className="w-3.5 h-3.5 text-gray-400" />
                </button>
              )}
            </div>
            <p className="text-xs text-gray-400 ml-auto shrink-0">
              {filtered.length} {t.newsCountSuffix}
            </p>
          </div>

          <div className="flex items-center gap-1.5 py-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            <button
              onClick={() => handleSector('All')}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all border shrink-0"
              style={{
                background: activeSector === 'All' ? '#166534' : 'white',
                color: activeSector === 'All' ? 'white' : '#6b7280',
                borderColor: activeSector === 'All' ? '#166534' : '#e2e8f0',
              }}
            >
              {t.all}
            </button>
            {SECTORS_21.map((s) => {
              const m = getSectorMeta(s);
              const isActive = activeSector === s;
              return (
                <button
                  key={s}
                  onClick={() => handleSector(s)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all border shrink-0"
                  style={{
                    background: isActive ? m.color : 'white',
                    color: isActive ? 'white' : '#6b7280',
                    borderColor: isActive ? m.color : '#e2e8f0',
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>

        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto px-6 py-10">

        {activeSector === 'All' && !query && featured && (
          <FeaturedCard article={featured} t={t} />
        )}

        <div className="flex flex-col lg:flex-row gap-8 mt-8">

          <div className="flex-1 min-w-0">
            {paginated.length === 0 ? (
              <div className="text-center py-20 rounded-2xl border border-slate-200 bg-white">
                <p className="text-4xl">📰</p>
                <p className="mt-3 font-semibold text-gray-900">{t.noResultsTitle}</p>
                <p className="mt-1 text-sm text-gray-400">{t.noResultsBody}</p>
                <button
                  onClick={() => { setQuery(''); setActiveSector('All'); }}
                  className="mt-4 px-4 py-2 rounded-lg text-sm bg-green-50 text-green-800 font-medium"
                >
                  {t.reset}
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {paginated.map((a) => <NewsCard key={a._id} article={a} t={t} />)}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="w-9 h-9 rounded-xl border border-slate-200 bg-white flex items-center justify-center hover:bg-gray-50 disabled:opacity-30 transition-all"
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-700" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className="w-9 h-9 rounded-xl border flex items-center justify-center text-sm font-medium transition-all"
                        style={{
                          background: page === p ? '#166534' : 'white',
                          color: page === p ? 'white' : '#111827',
                          borderColor: page === p ? '#166534' : '#e2e8f0',
                        }}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="w-9 h-9 rounded-xl border border-slate-200 bg-white flex items-center justify-center hover:bg-gray-50 disabled:opacity-30 transition-all"
                    >
                      <ChevronRight className="w-4 h-4 text-gray-700" />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <Sidebar trending={trending} recent={recent} t={t} />
        </div>
      </div>
    </div>
  );
}